import React from 'react';
import {
  BarChart3,
  CreditCard,
  Package,
  ShoppingBag,
  ShoppingCart,
  Star,
  TrendingUp
} from 'lucide-react';

export function EcommerceHeroMockups({ mock }) {
  return (
    <div className="relative mx-auto w-full max-w-xl lg:max-w-none lg:mx-0 min-h-[400px] sm:min-h-[460px] lg:min-h-[520px] overflow-x-hidden">
      <div
        className="absolute inset-0 rounded-[2rem] bg-gradient-to-br from-emerald-500/[0.08] via-transparent to-amber-500/[0.07] blur-2xl pointer-events-none animate-pulse"
        style={{ animationDuration: '4s' }}
        aria-hidden
      />

      {/* Loja virtual */}
      <div className="absolute left-0 top-6 sm:top-10 w-[min(100%,270px)] rounded-2xl border border-white/[0.12] bg-slate-950/85 backdrop-blur-xl shadow-[0_24px_80px_-24px_rgba(52,211,153,0.25)] p-4 z-20 rotate-[-2deg] hover:rotate-0 transition-transform duration-500">
        <div className="flex items-center gap-2 mb-3">
          <ShoppingBag className="w-4 h-4 text-emerald-400" aria-hidden />
          <span className="text-[10px] font-semibold uppercase tracking-wider text-white/45">{mock.storeLabel}</span>
        </div>
        <div className="grid grid-cols-2 gap-2 mb-2">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="rounded-lg bg-gradient-to-br from-emerald-500/15 to-slate-900/80 border border-emerald-500/15 p-2">
              <div className="aspect-square rounded-md bg-white/[0.06] mb-1.5" />
              <div className="h-1.5 w-3/4 rounded-full bg-white/15" />
            </div>
          ))}
        </div>
      </div>

      {/* Carrinho + checkout */}
      <div className="absolute right-0 top-0 w-[min(100%,250px)] rounded-2xl border border-white/[0.12] bg-slate-950/80 backdrop-blur-xl shadow-[0_20px_60px_-20px_rgba(251,191,36,0.25)] p-3 z-10 rotate-[2deg] sm:right-2 lg:right-6 hover:rotate-0 transition-transform duration-500">
        <div className="flex items-center gap-1.5 mb-2.5 px-1">
          <div className="w-2 h-2 rounded-full bg-red-400/80" />
          <div className="w-2 h-2 rounded-full bg-amber-400/80" />
          <div className="w-2 h-2 rounded-full bg-emerald-400/80" />
          <ShoppingCart className="w-3.5 h-3.5 text-amber-400 ml-auto" aria-hidden />
        </div>
        <div className="rounded-xl border border-white/[0.08] bg-gradient-to-br from-amber-500/10 to-slate-900/80 p-3">
          <span className="text-[10px] font-semibold text-white/70 block mb-2">{mock.cartLabel}</span>
          <div className="space-y-1.5 mb-2">
            <div className="h-6 rounded-md bg-white/[0.05] border border-white/[0.06] flex items-center px-2 gap-2">
              <div className="w-4 h-4 rounded bg-white/10 shrink-0" />
              <div className="h-1.5 flex-1 rounded-full bg-white/10" />
            </div>
            <div className="h-6 rounded-md bg-white/[0.04] border border-white/[0.05] flex items-center px-2 gap-2">
              <div className="w-4 h-4 rounded bg-white/10 shrink-0" />
              <div className="h-1.5 flex-1 rounded-full bg-white/[0.08]" />
            </div>
          </div>
          <div className="flex items-center gap-1.5 pt-2 border-t border-white/[0.08]">
            <CreditCard className="w-3.5 h-3.5 text-amber-300" aria-hidden />
            <span className="text-[9px] font-semibold text-amber-200/80">{mock.checkoutLabel}</span>
          </div>
        </div>
      </div>

      {/* Produto em destaque */}
      <div className="absolute left-8 sm:left-16 bottom-32 sm:bottom-36 w-[130px] rounded-[1.25rem] border border-white/[0.15] bg-slate-950/90 backdrop-blur-xl shadow-[0_16px_50px_-16px_rgba(0,0,0,0.8)] p-2 z-30 rotate-[4deg] hover:rotate-0 transition-transform duration-500">
        <div className="rounded-xl bg-gradient-to-b from-rose-500/15 to-slate-900/90 p-2 border border-white/[0.06]">
          <div className="flex items-center gap-1 mb-1.5">
            <Star className="w-3 h-3 text-amber-400 fill-amber-400/30" aria-hidden />
            <span className="text-[8px] font-semibold text-white/60">{mock.featuredLabel}</span>
          </div>
          <div className="h-14 rounded-lg bg-white/[0.06] border border-white/[0.05] mb-1.5" />
          <div className="h-1.5 w-full rounded-full bg-white/10 mb-1" />
          <div className="h-5 rounded-md bg-emerald-500/25 border border-emerald-500/30" />
        </div>
      </div>

      {/* Gráfico de vendas */}
      <div className="absolute right-8 sm:right-14 bottom-20 sm:bottom-24 w-[min(100%,200px)] rounded-xl border border-emerald-500/25 bg-emerald-950/40 backdrop-blur-md px-3 py-2.5 z-40 shadow-lg">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-emerald-400 shrink-0" aria-hidden />
          <div className="min-w-0">
            <p className="text-[10px] font-bold text-emerald-200/95">{mock.salesLabel}</p>
            <p className="text-[9px] text-emerald-200/55 flex items-center gap-1">
              <TrendingUp className="w-3 h-3" aria-hidden />
              {mock.metric} · {mock.live}
            </p>
          </div>
        </div>
      </div>

      {/* Painel de pedidos */}
      <div className="absolute left-1/2 -translate-x-1/2 bottom-4 sm:bottom-8 w-[min(100%,290px)] rounded-2xl border border-indigo-500/20 bg-gradient-to-br from-slate-950/95 to-slate-900/85 backdrop-blur-xl shadow-[0_28px_90px_-28px_rgba(99,102,241,0.25)] p-4 z-25">
        <div className="flex items-center justify-between gap-2 mb-3">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-indigo-200/80">{mock.ordersLabel}</span>
          <Package className="w-3.5 h-3.5 text-indigo-400" aria-hidden />
        </div>
        <div className="space-y-1.5">
          {['#1024', '#1023', '#1022'].map((id) => (
            <div key={id} className="flex items-center justify-between rounded-lg bg-white/[0.04] border border-white/[0.06] px-2.5 py-1.5">
              <span className="text-[9px] font-mono text-white/45">{id}</span>
              <span className="h-1.5 w-12 rounded-full bg-emerald-500/30" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
